import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";

import Loader from "../../components/common/Loader";
import ErrorState from "../../components/common/ErrorState";
import EmptyState from "../../components/common/EmptyState";
import applicationStatusService from "../../services/applicationStatusService";
import { FILE_BASE_URL } from "../../services/api";

const STATUS_OPTIONS = [
  { value: "shortlisted", label: "Shortlist", color: "success" },
  { value: "waitlisted", label: "Waitlist", color: "warning" },
  { value: "rejected", label: "Reject", color: "error" },
];

function statusLabel(status) {
  return status ? status.replace(/_/g, " ") : "—";
}

// Remount the round-specific view when the route changes.
// This prevents feedback from one round carrying into another.
export default function PanelRoundReview() {
  const { roundId } = useParams();

  return <RoundReviewTable key={roundId} roundId={roundId} />;
}

function RoundReviewTable({ roundId }) {
  const [students, setStudents] = useState([]);
  const [state, setState] = useState("loading");
  const [reloadKey, setReloadKey] = useState(0);

  const [activeStudent, setActiveStudent] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const savingRef = useRef(false);
  const mountedRef = useRef(false);
  const isMobile = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    setState("loading");

    applicationStatusService
      .roundStudents(roundId)
      .then((response) => {
        if (cancelled) return;

        if (!response.success || !Array.isArray(response.data)) {
          throw new Error("Could not load candidates.");
        }

        setStudents(response.data);
        setState(response.data.length ? "ready" : "empty");
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });

    return () => {
      cancelled = true;
    };
  }, [roundId, reloadKey]);

  function load() {
    setState("loading");
    setReloadKey((value) => value + 1);
  }

  function openFeedback(student) {
    if (savingRef.current) return;

    setActiveStudent(student);
    setFeedback("");
    setSaveError("");
  }

  function closeFeedback() {
    if (savingRef.current) return;

    setActiveStudent(null);
    setFeedback("");
    setSaveError("");
  }

  async function decide(status) {
    if (
      !activeStudent ||
      savingRef.current ||
      !STATUS_OPTIONS.some((option) => option.value === status)
    ) {
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setSaveError("");

    try {
      const response = await applicationStatusService.submitFeedback(
        roundId,
        activeStudent.id,
        { status, feedback }
      );

      if (!response.success) {
        throw new Error(response.message || "Could not save feedback.");
      }

      if (!mountedRef.current) return;

      setActiveStudent(null);
      setFeedback("");
      load();
    } catch (err) {
      if (!mountedRef.current) return;

      setSaveError(
        err.response?.data?.message ||
          err.friendlyMessage ||
          err.message ||
          "Could not save feedback. Please try again."
      );
    } finally {
      savingRef.current = false;

      if (mountedRef.current) setSaving(false);
    }
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Student name",
        size: 220,
        Cell: ({ row }) => (
          <Link
            to={`/panel/rounds/${roundId}/students/${row.original.id}`}
            className="pm-table__link"
            style={{ textDecoration: "underline", fontWeight: 600 }}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: "student_id",
        header: "Student ID",
        size: 150,
      },
      {
        accessorKey: "email_id",
        header: "Email",
        size: 250,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "specialisation",
        header: "Specialisation",
        size: 180,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
            {
        accessorKey: "tenth_score",
        header: "10th score",
        size: 130,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value === null || value === undefined || value === ""
            ? "—"
            : value;
        },
      },
      {
        accessorKey: "twelfth_score",
        header: "12th score",
        size: 130,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value === null || value === undefined || value === ""
            ? "—"
            : value;
        },
      },
      {
        accessorKey: "work_experience_years",
        header: "Work experience (years)",
        size: 210,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value === null || value === undefined || value === ""
            ? "—"
            : value;
        },
      },
           {
        accessorKey: "status",
        header: "Status",
        size: 170,
        filterVariant: "select",
        filterFn: "equals",
        filterSelectOptions: [
          { label: "Pending", value: "pending" },
          { label: "In review", value: "in_review" },
          { label: "Shortlisted", value: "shortlisted" },
          { label: "Waitlisted", value: "waitlisted" },
          { label: "Rejected", value: "rejected" },
        ],
        Cell: ({ row }) => (
          <span
            className={`pm-status-pill pm-status-pill--${row.original.status}`}
          >
            {statusLabel(row.original.status)}
          </span>
        ),
      },
      {
        id: "documents",
        header: "Documents",
        size: 150,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const student = row.original;

          return (
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              {student.cv && (
                <a
                  href={`${FILE_BASE_URL}/${student.cv}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pm-table__link"
                >
                  📄 CV
                </a>
              )}

              {student.video && (
                <a
                  href={`${FILE_BASE_URL}/${student.video}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pm-table__link"
                >
                  🎬 Video
                </a>
              )}

              {!student.cv && !student.video && "—"}
            </Box>
          );
        },
      },
      {
        accessorKey: "feedback",
        header: "Current round feedback",
        size: 280,
        Cell: ({ cell }) => (
          <Box
            sx={{
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              maxHeight: 140,
              overflowY: "auto",
            }}
          >
            {cell.getValue() || "—"}
          </Box>
        ),
      },
           {
        accessorKey: "previous_round_feedback",
        header: "All earlier round feedback",
        size: 360,
        Cell: ({ cell }) => (
          <Box
            sx={{
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              maxHeight: 240,
              overflowY: "auto",
              lineHeight: 1.6,
              pr: 1,
            }}
          >
            {cell.getValue() || "No earlier feedback"}
          </Box>
        ),
      },
    ],
    [roundId]
  );

  const table = useMaterialReactTable({
    columns,
    data: students,
    getRowId: (row) => String(row.id),
        enableRowNumbers: true,
    rowNumberDisplayMode: "static",

    enableRowActions: true,
    positionActionsColumn: "last",
    enableColumnResizing: true,
    enableFullScreenToggle: false,

          initialState: {
      density: "compact",
      showColumnFilters: true,
      pagination: { pageIndex: 0, pageSize: 10 },
    },

        displayColumnDefOptions: {
      "mrt-row-numbers": {
        header: "S. No.",
        Header: () => "S. No.",
        size: 80,
      },
      "mrt-row-actions": {
        header: "Actions",
        size: 200,
      },
    },

    renderRowActions: ({ row }) => (
      <Button
        type="button"
        variant="outlined"
        size="small"
        disabled={saving}
        onClick={() => openFeedback(row.original)}
        sx={{ textTransform: "none", whiteSpace: "normal" }}
      >
        Add feedback & decide
      </Button>
    ),

    muiTablePaperProps: {
      elevation: 0,
      sx: {
        width: "100%",
        minWidth: 0,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
      },
    },

    muiTableContainerProps: {
      sx: {
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
      },
    },

    muiTableBodyCellProps: {
      sx: {
        verticalAlign: "top",
        overflowWrap: "anywhere",
      },
    },

    muiTopToolbarProps: {
      sx: {
        "& > .MuiBox-root": {
          flexWrap: "wrap",
          gap: 1,
        },
      },
    },

    muiBottomToolbarProps: {
      sx: {
        "& > .MuiBox-root": {
          flexWrap: "wrap",
          gap: 1,
        },
        "& .MuiTablePagination-toolbar": {
          flexWrap: "wrap",
          justifyContent: "flex-end",
        },
      },
    },

    muiPaginationProps: {
      rowsPerPageOptions: [10, 25, 50],
    },
  });

  return (
    <div className="pm-page" style={{ minWidth: 0, maxWidth: "100%" }}>
      <Link to="/panel" className="pm-back-link">
        ← My rounds
      </Link>

      <header className="pm-page__header">
        <div>
          <span className="pm-eyebrow">Round review</span>
          <h1>Candidates in this round</h1>
        </div>
      </header>

      {state === "loading" && <Loader label="Loading candidates" />}

      {state === "error" && <ErrorState onRetry={load} />}

      {state === "empty" && (
        <EmptyState title="No candidates in this round yet" />
      )}

      {state === "ready" && (
        <Box sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}>
          <MaterialReactTable table={table} />
        </Box>
      )}

      <Dialog
        open={Boolean(activeStudent)}
        onClose={closeFeedback}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        disableEscapeKeyDown={saving}
        aria-labelledby="candidate-feedback-title"
      >
        <DialogTitle
          id="candidate-feedback-title"
          sx={{ overflowWrap: "anywhere" }}
        >
          Feedback: {activeStudent?.name}
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Current status: {statusLabel(activeStudent?.status)}
          </Typography>

          {activeStudent?.feedback && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: "action.hover",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2">
                Previous note
              </Typography>

              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {activeStudent.feedback}
              </Typography>
            </Box>
          )}

          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}

          <TextField
            label="Feedback (optional)"
            placeholder="Add feedback for the hiring team"
            helperText="Visible to the hiring team only."
            multiline
            minRows={4}
            fullWidth
            value={feedback}
            disabled={saving}
            onChange={(event) => setFeedback(event.target.value)}
          />
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            gap: 1,
            flexWrap: "wrap",
            "& > :not(style) ~ :not(style)": { marginLeft: 0 },
          }}
        >
          {STATUS_OPTIONS.map((option) => (
            <Button
              key={option.value}
              type="button"
              variant="contained"
              color={option.color}
              disabled={saving}
              onClick={() => decide(option.value)}
              sx={{ textTransform: "none" }}
            >
              {option.label}
            </Button>
          ))}

          <Button
            type="button"
            disabled={saving}
            onClick={closeFeedback}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>

          {saving && (
            <Typography variant="body2" role="status">
              Saving…
            </Typography>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}